const BASE_URL = process.env.REACT_APP_API_URL || 'https://cariya-wallet-api.onrender.com';

/**
 * Helper function to handle fetch responses and errors
 * @param {Response} response - The fetch response object 
 * @returns {Promise<Object>} - Parsed JSON response
 * @throws {Error} - If response is not OK
 */
async function handleResponse(response) {
  const rawResponse = await response.text();
  console.log('Raw API Response:', rawResponse);

  if (!response.ok) {
    const errorData = rawResponse.startsWith('{') ? JSON.parse(rawResponse) : {};
    const message = errorData.detail || `Request failed with status ${response.status}`;
    console.error('API Error Response:', {
      status: response.status,
      statusText: response.statusText,
      body: errorData,
    });
    throw new Error(message);
  }

  try {
    return rawResponse ? JSON.parse(rawResponse) : {};
  } catch (error) {
    console.error('Error parsing JSON response:', error.message);
    throw new Error('Failed to parse API response');
  }
}

/**
 * Validates month format (YYYY-MM)
 * @param {string} month - Month in YYYY-MM format
 * @returns {boolean} - True if valid
 */
function isValidMonth(month) {
  return /^\d{4}-(0[1-9]|1[0-2])$/.test(month);
}

/**
 * Partner login with email or phone number
 * @param {Object} loginData - Login data { email, phone_number }
 * @returns {Promise<Object>} - Response with message
 */
export async function partnerLogin(loginData) {
  try {
    const response = await fetch(`${BASE_URL}/partner/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(loginData),
    });
    const data = await handleResponse(response);
    console.log('Partner login response:', data);
    return data;
  } catch (error) {
    console.error('Error during partner login:', error.message);
    throw new Error(`Failed to login: ${error.message}`);
  }
}

/**
 * Verify partner with code
 * @param {Object} verifyData - Verification data { method, identifier, code }
 * @returns {Promise<Object>} - Response with partner_id and token
 */
export async function verifyCode(verifyData) {
  try {
    const response = await fetch(`${BASE_URL}/partner/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(verifyData),
    });
    const data = await handleResponse(response);
    console.log('Verification response:', data);
    return data;
  } catch (error) {
    console.error('Error during verification:', error.message);
    throw new Error(`Failed to verify code: ${error.message}`);
  }
}

/**
 * Get all partners
 * @returns {Promise<Object>} - List of partners
 */
export async function getPartners() {
  try {
    const response = await fetch(`${BASE_URL}/partners`, {
      method: 'GET',
      headers: { 'Accept': 'application/json' },
    });
    const data = await handleResponse(response);
    console.log('Fetched partners:', data);
    return data;
  } catch (error) {
    console.error('Error fetching partners:', error.message);
    throw new Error(`Failed to fetch partners: ${error.message}`);
  }
}

/**
 * Get partner profile by ID
 * @param {string} partnerId - Partner ID
 * @returns {Promise<Object>} - Partner profile
 */
export async function getPartnerProfile(partnerId) {
  if (!partnerId) throw new Error('Partner ID is required');
  try {
    const response = await fetch(`${BASE_URL}/partners/${partnerId}`, {
      method: 'GET',
      headers: { 'Accept': 'application/json' },
    });
    const data = await handleResponse(response);
    console.log('Fetched partner profile:', data);
    return data;
  } catch (error) {
    console.error('Error fetching partner profile:', error.message);
    throw new Error(`Failed to fetch partner profile: ${error.message}`);
  }
}

/**
 * Add a new partner
 * @param {Object} partnerData - Partner data
 * @returns {Promise<Object>} - Response with partner ID
 */
export async function addPartner(partnerData) {
  try {
    const response = await fetch(`${BASE_URL}/addPartner`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(partnerData),
    });
    const data = await handleResponse(response);
    console.log('Added partner:', data);
    return data;
  } catch (error) {
    console.error('Error adding partner:', error.message);
    throw new Error(`Failed to add partner: ${error.message}`);
  }
}

/**
 * Add a new partner subscription
 * @param {Object} subscriptionData - Subscription data
 * @returns {Promise<Object>} - Response with subscription ID
 */
export async function addPartnerSubscription(subscriptionData) {
  try {
    const response = await fetch(`${BASE_URL}/addPartnerSubscription`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(subscriptionData),
    });
    const data = await handleResponse(response);
    console.log('Added partner subscription:', data);
    return data;
  } catch (error) {
    console.error('Error adding partner subscription:', error.message);
    throw new Error(`Failed to add partner subscription: ${error.message}`);
  }
}

/**
 * Get partner subscription by partner ID
 * @param {string} partnerId - Partner ID
 * @returns {Promise<Object>} - Subscription details
 */
export async function getPartnerSubscription(partnerId) {
  if (!partnerId) throw new Error('Partner ID is required');
  try {
    const response = await fetch(`${BASE_URL}/partnerSubscriptions/${partnerId}`, {
      method: 'GET',
      headers: { 'Accept': 'application/json' },
    });
    const data = await handleResponse(response);
    console.log('Fetched partner subscription:', data);
    return data;
  } catch (error) {
    console.error('Error fetching partner subscription:', error.message);
    throw new Error(`Failed to fetch partner subscription: ${error.message}`);
  }
}

/**
 * Update a partner subscription
 * @param {string} subscriptionId - Subscription ID
 * @param {Object} subscriptionData - Updated subscription data
 * @returns {Promise<Object>} - Response with update status
 */
export async function updatePartnerSubscription(subscriptionId, subscriptionData) {
  if (!subscriptionId) throw new Error('Subscription ID is required');
  try {
    const response = await fetch(`${BASE_URL}/partnerSubscriptions/${subscriptionId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(subscriptionData),
    });
    const data = await handleResponse(response);
    console.log('Updated partner subscription:', data);
    return data;
  } catch (error) {
    console.error('Error updating partner subscription:', error.message);
    throw new Error(`Failed to update partner subscription: ${error.message}`);
  }
}

/**
 * Add a partner contribution
 * @param {Object} contributionData - Contribution data
 * @returns {Promise<Object>} - Response with contribution details
 */
export async function addPartnerContribution(contributionData) {
  try {
    const response = await fetch(`${BASE_URL}/addPartnerContribution`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(contributionData),
    });
    const data = await handleResponse(response);
    console.log('Added partner contribution:', data);
    return data;
  } catch (error) {
    console.error('Error adding partner contribution:', error.message);
    throw new Error(`Failed to add partner contribution: ${error.message}`);
  }
}

/**
 * Get all contributions for a partner
 * @param {string} partnerId - Partner ID
 * @returns {Promise<Object>} - List of contributions
 */
export async function getPartnerContributions(partnerId) {
  if (!partnerId) throw new Error('Partner ID is required');
  try {
    const response = await fetch(`${BASE_URL}/partners/${partnerId}/contributions`, {
      method: 'GET',
      headers: { 'Accept': 'application/json' },
    });
    const data = await handleResponse(response);
    console.log('Fetched partner contributions:', data);
    return data;
  } catch (error) {
    console.error('Error fetching partner contributions:', error.message);
    throw new Error(`Failed to fetch partner contributions: ${error.message}`);
  }
}

/**
 * Get all mothers
 * @returns {Promise<Object>} - List of mothers
 */
export async function getMothers() {
  try {
    const response = await fetch(`${BASE_URL}/mothers`, {
      method: 'GET',
      headers: { 'Accept': 'application/json' },
    });
    const data = await handleResponse(response);
    console.log('Fetched mothers:', data);
    return data;
  } catch (error) {
    console.error('Error fetching mothers:', error.message);
    throw new Error(`Failed to fetch mothers: ${error.message}`);
  }
}

/**
 * Get mother info by ID
 * @param {string} motherId - Mother ID
 * @returns {Promise<Object>} - Mother details
 */
export async function getMotherInfo(motherId) {
  if (!motherId) throw new Error('Mother ID is required');
  try {
    const response = await fetch(`${BASE_URL}/mothers/${motherId}`, {
      method: 'GET',
      headers: { 'Accept': 'application/json' },
    });
    const data = await handleResponse(response);
    console.log('Fetched mother info:', data);
    return data;
  } catch (error) {
    console.error('Error fetching mother info:', error.message);
    throw new Error(`Failed to fetch mother info: ${error.message}`);
  }
}

/**
 * Add a new mother
 * @param {Object} motherData - Mother data
 * @returns {Promise<Object>} - Response with mother ID
 */
export async function addMother(motherData) {
  try {
    const response = await fetch(`${BASE_URL}/addMother`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(motherData),
    });
    const data = await handleResponse(response);
    console.log('Added mother:', data);
    return data;
  } catch (error) {
    console.error('Error adding mother:', error.message);
    throw new Error(`Failed to add mother: ${error.message}`);
  }
}

/**
 * Add savings for a mother
 * @param {string} motherId - Mother ID
 * @param {Object} savingsData - Savings data
 * @returns {Promise<Object>} - Response with savings details
 */
export async function addSavings(motherId, savingsData) {
  if (!motherId) throw new Error('Mother ID is required');
  try {
    const response = await fetch(`${BASE_URL}/mothers/${motherId}/savings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(savingsData),
    });
    const data = await handleResponse(response);
    console.log('Added savings:', data);
    return data;
  } catch (error) {
    console.error('Error adding savings:', error.message);
    throw new Error(`Failed to add savings: ${error.message}`);
  }
}

/**
 * Get all donors
 * @returns {Promise<Object>} - List of donors
 */
export async function getDonors() {
  try {
    const response = await fetch(`${BASE_URL}/donors`, {
      method: 'GET',
      headers: { 'Accept': 'application/json' },
    });
    const data = await handleResponse(response);
    console.log('Fetched donors:', data);
    return data;
  } catch (error) {
    console.error('Error fetching donors:', error.message);
    throw new Error(`Failed to fetch donors: ${error.message}`);
  }
}

/**
 * Get donor profile by ID
 * @param {string} donorId - Donor ID
 * @returns {Promise<Object>} - Donor profile
 */
export async function getDonorProfile(donorId) {
  if (!donorId) throw new Error('Donor ID is required');
  try {
    const response = await fetch(`${BASE_URL}/donors/${donorId}`, {
      method: 'GET',
      headers: { 'Accept': 'application/json' },
    });
    const data = await handleResponse(response);
    console.log('Fetched donor profile:', data);
    return data;
  } catch (error) {
    console.error('Error fetching donor profile:', error.message);
    throw new Error(`Failed to fetch donor profile: ${error.message}`);
  }
}

/**
 * Add a new donor
 * @param {Object} donorData - Donor data
 * @returns {Promise<Object>} - Response with donor ID
 */
export async function addDonor(donorData) {
  try {
    const response = await fetch(`${BASE_URL}/addDonor`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(donorData),
    });
    const data = await handleResponse(response);
    console.log('Added donor:', data);
    return data;
  } catch (error) {
    console.error('Error adding donor:', error.message);
    throw new Error(`Failed to add donor: ${error.message}`);
  }
}

/**
 * Update a donor
 * @param {string} donorId - Donor ID
 * @param {Object} donorData - Updated donor data
 * @returns {Promise<Object>} - Response with update status
 */
export async function updateDonor(donorId, donorData) {
  if (!donorId) throw new Error('Donor ID is required');
  try {
    const response = await fetch(`${BASE_URL}/donors/${donorId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(donorData),
    });
    const data = await handleResponse(response);
    console.log('Updated donor:', data);
    return data;
  } catch (error) {
    console.error('Error updating donor:', error.message);
    throw new Error(`Failed to update donor: ${error.message}`);
  }
}

/**
 * Delete a donor
 * @param {string} donorId - Donor ID
 * @returns {Promise<Object>} - Response with deletion status
 */
export async function deleteDonor(donorId) {
  if (!donorId) throw new Error('Donor ID is required');
  try {
    const response = await fetch(`${BASE_URL}/donors/${donorId}`, {
      method: 'DELETE',
      headers: { 'Accept': 'application/json' },
    });
    const data = await handleResponse(response);
    console.log('Deleted donor:', data);
    return data;
  } catch (error) {
    console.error('Error deleting donor:', error.message);
    throw new Error(`Failed to delete donor: ${error.message}`);
  }
}

/**
 * Add a donation
 * @param {Object} donationData - Donation data
 * @returns {Promise<Object>} - Response with donation details
 */
export async function addDonation(donationData) {
  try {
    const response = await fetch(`${BASE_URL}/addDonation`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(donationData),
    });
    const data = await handleResponse(response);
    console.log('Added donation:', data);
    return data;
  } catch (error) {
    console.error('Error adding donation:', error.message);
    throw new Error(`Failed to add donation: ${error.message}`);
  }
}

/**
 * Get all donations for a donor
 * @param {string} donorId - Donor ID
 * @returns {Promise<Object>} - List of donations
 */
export async function getDonorDonations(donorId) {
  if (!donorId) throw new Error('Donor ID is required');
  try {
    const response = await fetch(`${BASE_URL}/donors/${donorId}/donations`, {
      method: 'GET',
      headers: { 'Accept': 'application/json' },
    });
    const data = await handleResponse(response);
    console.log('Fetched donor donations:', data);
    return data;
  } catch (error) {
    console.error('Error fetching donor donations:', error.message);
    throw new Error(`Failed to fetch donor donations: ${error.message}`);
  }
}

/**
 * Get all donations for a mother
 * @param {string} motherId - Mother ID
 * @returns {Promise<Object>} - List of donations
 */
export async function getMotherDonations(motherId) {
  if (!motherId) throw new Error('Mother ID is required');
  try {
    const response = await fetch(`${BASE_URL}/mothers/${motherId}/donations`, {
      method: 'GET',
      headers: { 'Accept': 'application/json' },
    });
    const data = await handleResponse(response);
    console.log('Fetched mother donations:', data);
    return data;
  } catch (error) {
    console.error('Error fetching mother donations:', error.message);
    throw new Error(`Failed to fetch mother donations: ${error.message}`);
  }
}

/**
 * Get donor view
 * @returns {Promise<Object>} - Donor view data
 */
export async function getDonorView() {
  try {
    const response = await fetch(`${BASE_URL}/donor-view`, {
      method: 'GET',
      headers: { 'Accept': 'application/json' },
    });
    const data = await handleResponse(response);
    console.log('Fetched donor view:', data);
    return data;
  } catch (error) {
    console.error('Error fetching donor view:', error.message);
    throw new Error(`Failed to fetch donor view: ${error.message}`);
  }
}


/**
 * Get monthly savings for all mothers
 * @param {Object} params - Query parameters
 * @param {string} [params.month] - Month in YYYY-MM format
 * @returns {Promise<Object>} - Monthly savings data
 */
export async function getAllMonthlySavings({ month } = {}) {
  if (month && !isValidMonth(month)) {
    throw new Error('Invalid month format. Expected YYYY-MM');
  }
  const query = new URLSearchParams();
  if (month) query.append('month', month);

  try {
    const response = await fetch(`${BASE_URL}/monthly-savings?${query.toString()}`, {
      method: 'GET',
      headers: { 'Accept': 'application/json' },
    });
    const data = await handleResponse(response);
    console.log('Fetched monthly savings:', data);
    return data;
  } catch (error) {
    console.error('Error fetching monthly savings:', error.message);
    throw new Error(`Failed to fetch monthly savings: ${error.message}`);
  }
}

/**
 * Get monthly activities for a mother
 * @param {string} motherId - Mother ID
 * @returns {Promise<Object>} - Monthly activities
 */
export async function getMonthlyActivities(motherId) {
  if (!motherId) throw new Error('Mother ID is required');
  try {
    const response = await fetch(`${BASE_URL}/mothers/${motherId}/monthly-activities`, {
      method: 'GET',
      headers: { 'Accept': 'application/json' },
    });
    const data = await handleResponse(response);
    console.log('Fetched monthly activities:', data);
    return data;
  } catch (error) {
    console.error('Error fetching monthly activities:', error.message);
    throw new Error(`Failed to fetch monthly activities: ${error.message}`);
  }
}

/**
 * Get compliance score for a mother
 * @param {string} motherId - Mother ID
 * @returns {Promise<Object>} - Compliance score
 */
export async function getCompliance(motherId) {
  if (!motherId) throw new Error('Mother ID is required');
  try {
    const response = await fetch(`${BASE_URL}/mothers/${motherId}/compliance`, {
      method: 'GET',
      headers: { 'Accept': 'application/json' },
    });
    const data = await handleResponse(response);
    console.log('Fetched compliance:', data);
    return data;
  } catch (error) {
    console.error('Error fetching compliance:', error.message);
    throw new Error(`Failed to fetch compliance: ${error.message}`);
  }
}

/**
 * Add a new activity
 * @param {Object} activityData - Activity data
 * @returns {Promise<Object>} - Response with activity ID
 */
export async function addActivity(activityData) {
  try {
    const response = await fetch(`${BASE_URL}/addActivity`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(activityData),
    });
    const data = await handleResponse(response);
    console.log('Added activity:', data);
    return data;
  } catch (error) {
    console.error('Error adding activity:', error.message);
    throw new Error(`Failed to add activity: ${error.message}`);
  }
}

/**
 * Get mothers by partner ID
 * @param {string} partnerId - Partner ID
 * @returns {Promise<Object>} - Mothers data with partner information
 */
export async function getMothersByPartner(partnerId) {
  if (!partnerId) throw new Error('Partner ID is required');
  try {
    const response = await fetch(`${BASE_URL}/partners/${partnerId}/mothers`, {
      method: 'GET',
      headers: { 'Accept': 'application/json' },
    });
    const data = await handleResponse(response);
    console.log('Fetched mothers by partner:', data);
    return data;
  } catch (error) {
    console.error('Error fetching mothers by partner:', error.message);
    throw new Error(`Failed to fetch mothers by partner: ${error.message}`);
  }
}